<script lang="ts">
    import {
        VpEqualizerIcon,
        VpSearchIcon,
    } from '@decathlon-experience/icons/svelte';
    import { createDialog, melt } from '@melt-ui/svelte';
    import { onDestroy, onMount } from 'svelte';
    import { fade } from 'svelte/transition';

    import { trans } from '$assets/shop-translator.js';
    import cn from '$common/utils/cn';
    import viewport from '$saga/actions/useViewport';
    import { flyDrawer } from '$saga/utils/customTransitions';
    import MainFiltersDrawer from '$saga/views/Search/MainFilters/drawer/MainFiltersDrawer.svelte';
    import FiltersBar from '$saga/views/Search/MainFilters/FiltersBar.svelte';
    import FiltersBarHomeMobile from '$saga/views/Search/MainFilters/FiltersBarHomeMobile.svelte';
    import SecondaryFiltersDrawer from '$saga/views/Search/SecondaryFilters/SecondaryFiltersDrawer.svelte';
    import Sort from '$saga/views/Search/Sort.svelte';
    import {
        areMainFiltersActive,
        areSecondaryFiltersActive,
        filtersContext,
        mainFiltersModalOpen,
        mainFiltersSelectedFilterType,
        secondaryFiltersModalOpen,
    } from '$saga/views/Search/store';
    import { FilterType } from '$saga/views/Search/types';

    let isSticky = false;
    let lastScrollTop = 0;
    let isHidden = false;
    let mainFiltersButton: HTMLDivElement | null = null;
    let mainFiltersBarOriginalTop = 0;

    // Compute menu title based on selected filter type
    $: mainFiltersMenuTitle = (() => {
        switch ($mainFiltersSelectedFilterType) {
            case FilterType.DESTINATION:
                return trans('search.bar.search_destination');
            case FilterType.SPORT:
                return trans('search.bar.search_sport');
            case FilterType.PAX:
                return trans('search.bar.travelers');
            case FilterType.STARTDATE:
                return trans('search.bar.departure_date');
            default:
                return trans('search.bar.search');
        }
    })();

    const handleScroll = () => {
        if (!mainFiltersButton) return;
        const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;

        // Only apply hide/show logic if we've scrolled past the original position
        // This ensures the bar is already sticky before we start hiding it
        if (scrollTop > mainFiltersBarOriginalTop) {
            isHidden = scrollTop > lastScrollTop;
        } else {
            // We're above the original position, always show
            isHidden = false;
        }
        lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    };

    onMount(() => {
        // Store the original position of the mainFiltersBar
        if (mainFiltersButton) {
            mainFiltersBarOriginalTop = mainFiltersButton.offsetTop + 88;
        }
        window.addEventListener('scroll', handleScroll, { passive: true });
    });

    onDestroy(() => {
        window.removeEventListener('scroll', handleScroll);
    });

    const handleSelectedFilter = async (filterType: FilterType) => {
        mainFiltersSelectedFilterType.set(filterType);
    };

    const resetView = () => {
        if (!$filtersContext.isHomePage) $mainFiltersModalOpen = false;
        mainFiltersSelectedFilterType.set(FilterType.SELECTION);
    };

    const {
        elements: { trigger, title, overlay, content, close, portalled },
        states: { open },
    } = createDialog({
        open: mainFiltersModalOpen,
        forceVisible: true,
    });

    const {
        elements: {
            trigger: secondaryTrigger,
            title: secondaryTitle,
            overlay: secondaryOverlay,
            content: secondaryContent,
            close: secondaryClose,
            portalled: secondaryPortalled,
        },
        states: { open: secondaryModalOpen },
    } = createDialog({
        open: secondaryFiltersModalOpen,
        forceVisible: true,
    });
</script>

<div
    use:viewport
    on:enterViewport={() => {
        if (!$filtersContext.isHomePage) isSticky = false;
    }}
    on:exitViewport={() => {
        if (!$filtersContext.isHomePage) isSticky = true;
    }}
></div>

<!--
    used as a placeholder when filters button in mobile are sticky
    to avoid layout shift
 -->
{#if isSticky && !$filtersContext.isHomePage}
    <div
        class="desktop:hidden"
        style="height: {mainFiltersButton?.clientHeight ?? '88'}px"
    ></div>
{/if}

<!-- mobile buttons -->
{#if !$filtersContext.isHomePage}
    <div
        bind:this={mainFiltersButton}
        class={cn(
            'relative flex gap-xs py-l transition-transform duration-300 desktop:hidden',
            isSticky &&
                'fixed left-0 top-0 z-10 w-full bg-container-quiet px-l',
            isHidden && '-translate-y-full',
        )}
    >
        <button
            class={cn(
                'relative top-0 z-20 flex h-8 w-full transform items-center justify-center gap-2xs rounded-full px-m py-xs text-xs',
                'border border-solid border-border-neutral bg-white',
            )}
            use:melt={$trigger}
        >
            <span class="flex items-center gap-2xs">
                <VpSearchIcon class="size-l" />
                <span>{trans('common.edit')}</span>
                {#if $areMainFiltersActive}
                    <div class="h-xl w-fit">
                        <div
                            class="mb-auto size-[7px] rounded-full bg-container-brand"
                        ></div>
                    </div>
                {/if}
            </span>
        </button>
        <button
            class={cn(
                'relative top-0 z-20 flex h-8 w-full transform items-center justify-center gap-2xs rounded-full px-m py-xs text-xs',
                'border border-solid border-border-neutral bg-white',
            )}
            use:melt={$secondaryTrigger}
        >
            <span class="flex items-center gap-2xs">
                <VpEqualizerIcon class="size-l" />
                <span>{trans('search.bar.filter')}</span>
                {#if $areSecondaryFiltersActive}
                    <div class="h-xl w-fit">
                        <div
                            class="mb-auto size-[7px] rounded-full bg-container-brand"
                        ></div>
                    </div>
                {/if}
            </span>
        </button>
        <Sort />
    </div>
{/if}

<!-- TODO MOBILE ONLY FOR NOW-->
{#if $filtersContext.isHomePage}
    <FiltersBarHomeMobile {trigger} {handleSelectedFilter} />
{/if}

<FiltersBar {trigger} {handleSelectedFilter} />

{#if $open}
    <div use:melt={$portalled}>
        <div
            use:melt={$overlay}
            class="fixed inset-0 z-40 bg-black/50"
            transition:fade={{ duration: 250 }}
        ></div>
        <div
            use:melt={$content}
            class="fixed inset-x-0 bottom-0 z-50 flex h-full max-h-[calc(100%)] w-full flex-col bg-white transition-all duration-500 desktop:inset-x-[unset] desktop:right-0 desktop:top-0 desktop:min-w-[33vw] desktop:max-w-[33vw]"
            transition:flyDrawer
        >
            <MainFiltersDrawer
                selectedFilterType={$mainFiltersSelectedFilterType}
                {handleSelectedFilter}
                {mainFiltersMenuTitle}
                {title}
                {close}
                {resetView}
            />
        </div>
    </div>
{/if}

{#if $secondaryModalOpen}
    <div use:melt={$secondaryPortalled}>
        <div
            use:melt={$secondaryOverlay}
            class="fixed inset-0 z-40 bg-black/50"
            transition:fade={{ duration: 250 }}
        ></div>
        <div
            use:melt={$secondaryContent}
            class="fixed inset-x-0 bottom-0 z-50 flex h-full max-h-[calc(100%)] w-full flex-col bg-white transition-all duration-500 desktop:inset-x-[unset] desktop:right-0 desktop:top-0 desktop:min-w-[33vw] desktop:max-w-[33vw]"
            transition:flyDrawer
        >
            <SecondaryFiltersDrawer
                title={secondaryTitle}
                close={secondaryClose}
                {resetView}
            />
        </div>
    </div>
{/if}
